// EDEN · tiny API client. Always sends the session cookie.
//
// 2026-09-30: a short-lived memory cache for a FEW slow-changing reads (the current term, the class
// lists, the calendar's key dates). Each page asked for them again on every click, and every
// request is a 0.2–1 s round trip from Cambodia (measured on the owner's PC) — that waiting is most
// of why a click felt dead. Rules that keep it honest:
//   • only the paths in CACHEABLE are ever cached, for at most 60 s, in this tab's memory only
//     (never localStorage, never the service worker — the API-never-cached rail in sw.js stands);
//   • ANY non-GET request (a save, a submit, a delete) empties the whole cache first, so the user
//     always sees their own change on the next screen;
//   • signing out empties it too (the session is gone; nothing may outlive it).

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

const TTL_MS = 60_000;
const CACHEABLE = [
  /^\/api\/terms\/current$/,
  /^\/api\/classes(\?scope=own)?$/,
  /^\/api\/calendar\?scope=key$/,
];
const cache = new Map<string, { at: number; data: unknown }>();

/** Forget every cached read (called on any write and on sign-out). */
export function clearApiCache(): void {
  cache.clear();
}

export async function api<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const cacheable = method === "GET" && CACHEABLE.some((re) => re.test(path));
  if (method !== "GET") clearApiCache();
  if (cacheable) {
    const hit = cache.get(path);
    if (hit && Date.now() - hit.at < TTL_MS) return structuredClone(hit.data) as T;
  }
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers ?? {}) },
    ...opts,
  });
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    // A server that fails without saying why (Cloudflare's own error page, e.g. the database's daily
    // limit) gets a calm, true sentence instead of "Something went wrong." — nothing the teacher did.
    // A read (a page opening) has no change to lose, so only a write is told it may not be saved.
    const isRead = !opts.method || opts.method.toUpperCase() === "GET";
    const fallback = res.status >= 500
      ? isRead
        ? "EDEN's server could not answer just now. Try again in a few minutes. If it keeps happening, tell your Lead."
        : "EDEN's server could not answer just now, so this last change may not be saved yet. Keep this page open and try again in a few minutes. If it keeps happening, tell your Lead."
      : "Something went wrong.";
    const msg = (data as { error?: string })?.error ?? fallback;
    throw new ApiError(res.status, msg);
  }
  if (cacheable) cache.set(path, { at: Date.now(), data: structuredClone(data) });
  return data as T;
}
